import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectSession } from '@client/model/selectors';
import { analytics } from '@client/analytics';
import { nylMetricsApi } from '@client/api/waterlily';
import { MAX_ADVISOR_ACTIVE_INTERVAL_SECONDS, newUuid, SessionType } from '@shared';

export const ADVISOR_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
export const ACTIVE_TIME_TICK_MS = 15 * 1000;
const MAX_COUNTABLE_TICK_GAP_MS = ACTIVE_TIME_TICK_MS * 3;
const MINUTE_MS = 60 * 1000;

interface ActiveBucket {
  milliseconds: number;
  sentSeconds: number;
}

/**
 * Records prospective, non-idle platform time for advisors. The authenticated endpoint derives
 * the advisor ID from the session; the browser sends only a minute bucket and its cumulative active
 * seconds. The server keeps the largest value for an advisor-minute, deduplicating retries and tabs.
 */
export function useAdvisorActiveTimeTracking(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;

    const buckets = new Map<number, ActiveBucket>();
    let lastActivityAt = Date.now();
    let lastCheckAt = lastActivityAt;

    const recordInterval = (startAt: number, endAt: number) => {
      let cursor = startAt;
      while (cursor < endAt) {
        const bucketStart = Math.floor(cursor / MINUTE_MS) * MINUTE_MS;
        const bucketEnd = bucketStart + MINUTE_MS;
        const intervalEnd = Math.min(endAt, bucketEnd);
        const bucket = buckets.get(bucketStart) ?? { milliseconds: 0, sentSeconds: 0 };
        bucket.milliseconds = Math.min(MINUTE_MS, bucket.milliseconds + intervalEnd - cursor);
        buckets.set(bucketStart, bucket);
        cursor = intervalEnd;
      }
    };

    const flushBucket = (bucketStart: number) => {
      const bucket = buckets.get(bucketStart);
      if (!bucket) return;
      const activeSeconds = Math.min(
        MAX_ADVISOR_ACTIVE_INTERVAL_SECONDS,
        Math.floor(bucket.milliseconds / 1000),
      );
      if (activeSeconds < 1 || activeSeconds <= bucket.sentSeconds) return;

      bucket.sentSeconds = activeSeconds;
      void nylMetricsApi
        .recordActiveTime({
          eventId: newUuid(),
          eventName: 'advisor_active_interval',
          occurredAt: new Date(bucketStart).toISOString(),
          activeSeconds,
        })
        .catch(() => {
          // This is best-effort telemetry. A later cumulative update for the same minute retries
          // naturally; the dashboard must never interrupt advisor work when capture is unavailable.
          bucket.sentSeconds = 0;
        });
    };

    const flushCompletedBuckets = (now: number) => {
      const currentBucket = Math.floor(now / MINUTE_MS) * MINUTE_MS;
      for (const bucketStart of buckets.keys()) {
        if (bucketStart < currentBucket) flushBucket(bucketStart);
        if (bucketStart < currentBucket - 5 * MINUTE_MS) buckets.delete(bucketStart);
      }
    };

    const tick = (now = Date.now(), countLastVisibleInterval = false) => {
      const elapsed = now - lastCheckAt;
      if (
        (document.visibilityState === 'visible' || countLastVisibleInterval) &&
        elapsed > 0 &&
        elapsed <= MAX_COUNTABLE_TICK_GAP_MS
      ) {
        const activeUntil = Math.min(now, lastActivityAt + ADVISOR_IDLE_TIMEOUT_MS);
        if (activeUntil > lastCheckAt) recordInterval(lastCheckAt, activeUntil);
      }
      // A large jump means the laptop slept or the browser suspended timers. None of the gap is
      // counted because we cannot prove the advisor was actively using the platform.
      lastCheckAt = now;
      flushCompletedBuckets(now);
    };

    const markActivity = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      tick(now);
      lastActivityAt = now;
    };

    const handleVisibilityChange = () => {
      const now = Date.now();
      if (document.visibilityState === 'hidden') {
        // The visibilitychange event fires after the document becomes hidden. This interval was
        // visible until the transition, so count it explicitly before stopping the clock.
        tick(now, true);
        for (const bucketStart of buckets.keys()) flushBucket(bucketStart);
        return;
      }
      lastActivityAt = now;
      lastCheckAt = now;
    };

    const handlePageHide = () => {
      tick();
      for (const bucketStart of buckets.keys()) flushBucket(bucketStart);
    };

    const interval = window.setInterval(() => tick(), ACTIVE_TIME_TICK_MS);
    const activityEvents: (keyof DocumentEventMap)[] = [
      'pointerdown',
      'keydown',
      'scroll',
      'touchstart',
    ];
    activityEvents.forEach(eventName =>
      document.addEventListener(eventName, markActivity, { passive: true }),
    );
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      tick();
      for (const bucketStart of buckets.keys()) flushBucket(bucketStart);
      window.clearInterval(interval);
      activityEvents.forEach(eventName => document.removeEventListener(eventName, markActivity));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [enabled]);
}

export function AppOpenTracker() {
  const session = useSelector(selectSession);
  const hasTracked = useRef(false);
  useAdvisorActiveTimeTracking(
    session.sessionType === SessionType.advisor &&
      session.advisor?.featureFlags?.nylPilotAdvisor === true,
  );

  useEffect(() => {
    // Only track once per page load, and wait until session is loaded
    if (hasTracked.current) return;
    if (session.isLoading) return;
    if (session.sessionType === SessionType.unknown) return;

    hasTracked.current = true;

    const isFirstOpen = !sessionStorage.getItem('app_opened');
    if (isFirstOpen) {
      sessionStorage.setItem('app_opened', 'true');
    }

    analytics.appOpen({
      entryPoint: document.referrer ? 'referral' : 'direct',
      isFirstOpen,
    });
  }, [session.isLoading, session.sessionType]);

  return null;
}
